import * as React from "react";
import { Autocomplete, Checkbox, FormControlLabel, TextField } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import ConfirmModal from "../confirm.modal";
import type { ModalComponentProps } from "../../../types/modal.types";
import type { newQuotationForm } from "../../../lib/apis/dtos/request/quotation.request";
import { usePartnerStoreList } from "../../../lib/apis/hooks/partner.hook";
import { getPartnerQuotationReference } from "../../../lib/apis/v1/partner.service";
import { mergeImportedQuotation } from "../../../utils/quotation-import.utils";
import FailSnackbar from "../../ui/snackbar/fail.component";
import SuccessSnackbar from "../../ui/snackbar/success.component";

interface QuotationImportModalProps extends ModalComponentProps {
  /** 현재 편집 중인 폼 — 메타 정보 유지용 */
  currentForm: newQuotationForm;
  /** 가져온 결과를 편집 폼에 반영 (제출 아님) */
  onImport: (next: newQuotationForm) => void;
  /** 제휴 업체에 연결된 공급업체 — 있으면 대상 지점 기본 선택 */
  defaultStore?: StoreOption | null;
}

interface StoreOption {
  id: number;
  storeName: string;
}

/** "+12.1" / "-3" / "12" → 숫자. 비어 있거나 부호만이면 undefined */
const parseAmount = (raw: string): number | undefined => {
  const n = Number(raw.replace(/,/g, ""));
  return raw === "" || raw === "+" || raw === "-" || !Number.isFinite(n) ? undefined : n;
};

/**
 * 시세표 가져오기 — 다른 업체(지점)의 시세표를 만원 단위 ± 가감해서 현재 폼에 복사.
 * 가감 계산은 백엔드(GET /v1/partner/quotation/reference/{storeId}) 가 담당하고,
 * 서버에 제출하지 않는다.
 */
export default function QuotationImportModal({ id, onClose, currentForm, onImport, defaultStore = null }: QuotationImportModalProps) {
  const [selected, setSelected] = React.useState<StoreOption | null>(defaultStore);
  const [amountText, setAmountText] = React.useState("");
  const [useAddService, setUseAddService] = React.useState(true);
  const [loading, setLoading] = React.useState(false);

  // 공급업체 후보 목록 — 제휴 업체 공급업체 선택과 같은 소스 (GET /v1/partner/store)
  const { data, isSuccess } = useQuery(usePartnerStoreList());
  const options: StoreOption[] = (data?.result || []).map((s) => ({ id: s.id, storeName: s.storeName }));

  const changeAmount = (e: React.ChangeEvent<HTMLInputElement>) => {
    let val = e.target.value.replace(/,/g, "");
    if (/^\d/.test(val)) val = `+${val}`;
    if (/^[-+]?\d*(\.\d{0,1})?$/.test(val)) setAmountText(val);
  };

  const handleImport = async () => {
    if (!selected) return;
    setLoading(true);
    try {
      const res = await getPartnerQuotationReference({
        storeId: selected.id,
        addAmount: parseAmount(amountText),
        useAddService,
      });
      if (res.error || !res.result) {
        FailSnackbar(res.message || "시세표 불러오기 실패");
        return;
      }
      onImport(mergeImportedQuotation(res.result, currentForm));
      SuccessSnackbar(`${selected.storeName} 시세표를 가져왔습니다. 확인 후 "시세표 제출"을 눌러주세요.`);
      onClose?.();
    } catch {
      FailSnackbar("시세표 불러오기 실패");
    } finally {
      setLoading(false);
    }
  };

  return (
    <ConfirmModal
      id={id}
      onClose={onClose}
      className="w-[760px]"
      title="시세표 가져오기"
      rightButton={{
        title: loading ? "가져오는 중..." : "시세표 가져오기",
        disabled: !selected || loading,
        variant: !selected || loading ? "disabled" : "primary",
        onClick: handleImport,
      }}
    >
      <p className="text-[16px] py-4 text-gray-60">가져올 업체의 최근 제출 시세표를 현재 시세표에 복사합니다.</p>

      <div className="flex-row gap-3 items-center">
        <Autocomplete
          className="w-[320px]"
          options={options}
          loading={!isSuccess}
          value={selected}
          getOptionLabel={(o) => o.storeName}
          isOptionEqualToValue={(a, b) => a.id === b.id}
          onChange={(_, v) => setSelected(v)}
          disablePortal
          slotProps={{ popper: { sx: { zIndex: 9999 } } }}
          renderOption={(props, option) => (
            <li {...props} key={option.id}>
              {option.storeName}
            </li>
          )}
          renderInput={(params) => (
            <TextField {...params} label="가져올 업체 선택" placeholder="지점명으로 검색" slotProps={{ inputLabel: { shrink: true } }} />
          )}
        />
        <span className="text-[20px] font-semibold">+</span>
        <TextField
          className="w-[120px]"
          value={amountText}
          onChange={changeAmount}
          inputProps={{ maxLength: 6 }}
          slotProps={{ input: { placeholder: "0", sx: { height: "40px" } } }}
        />
        <span className="whitespace-nowrap">만원으로</span>
      </div>

      <FormControlLabel
        className="mt-2"
        label="부가서비스도 함께 가져오기"
        control={<Checkbox checked={useAddService} onChange={(e) => setUseAddService(e.target.checked)} />}
      />

      <p className="mt-2 text-[13px] whitespace-pre-line text-black/60">
        {`*'만원' 단위로 입력해주세요. 소수점 한자리까지(천원 단위까지) 표기 가능합니다. 비워두면 원본 가격 그대로 복사됩니다.
*현재 입력한 값들이 있다면 모두 사라집니다.`}
      </p>
      <p className="mt-3 text-right text-[14px] text-[#B3261E]">※ 해당 가져오기는 제출이 아닌 복사입니다.</p>
    </ConfirmModal>
  );
}
